var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import Extent from '@arcgis/core/geometry/Extent';
import { assertGeographic, WGS84 } from '../constants';
// Mirror the server's POINTS_MIN_ZOOM and POINTS_MAX_EXTENT. Outside those bounds the
// map-point query answers with grid cells sized from the requested extent.
const POINTS_MIN_ZOOM = 12;
const POINTS_MAX_EXTENT = 2.0;
// Multiplier grown around the viewport to give panning room.
const VIEWPORT_PADDING = 1.75;
const FETCH_DEBOUNCE_MS = 500;
const extentSpan = (extent) => Math.max(extent.width, extent.height);
const isPointMode = (extent, zoom) => zoom >= POINTS_MIN_ZOOM && extentSpan(extent) <= POINTS_MAX_EXTENT;
// Lattice width in map tiles, halving per zoom level so every zoom has its own fixed grid.
// Raise it to trade coarser cells for fewer reloads.
const LATTICE_TILES = 2;
const latticeSize = (zoom) => LATTICE_TILES * 360 / Math.pow(2, Math.max(0, Math.min(22, zoom)));
// Rounds the extent out onto the zoom's lattice, so panning re-asks for an identical rectangle.
const snappedExtent = (extent, zoom) => {
    const size = latticeSize(zoom);
    return assertGeographic(new Extent({
        'xmin': Math.floor(extent.xmin / size) * size,
        'ymin': Math.floor(extent.ymin / size) * size,
        'xmax': Math.ceil(extent.xmax / size) * size,
        'ymax': Math.ceil(extent.ymax / size) * size,
        'spatialReference': WGS84(),
    }));
};
// Point requests get padding, trimmed to stay inside the aggregation bound. Aggregated
// requests get the lattice instead.
const requestExtent = (mapView) => {
    const geo = assertGeographic(mapView.extent);
    const zoom = Math.round(mapView.zoom);
    const span = extentSpan(geo);
    return !isPointMode(geo, zoom)
        ? snappedExtent(geo, zoom)
        : assertGeographic(geo.clone().expand(Math.min(VIEWPORT_PADDING, Math.max(1, POINTS_MAX_EXTENT / span))));
};
// A viewport inside the loaded rectangle is already drawn. Aggregated cells only describe the
// zoom they were built for.
const isCovered = (coverage, extent, zoom, filters, loadKey) => !!coverage
    && coverage.filters === filters
    && Object.is(coverage.loadKey, loadKey)
    && coverage.pointMode === isPointMode(extent, zoom)
    && (coverage.pointMode || coverage.zoom === zoom)
    && coverage.extent.contains(extent);
const sameKeys = (a, b) => a.size === b.size && Array.from(a.keys()).every(id => b.has(id));
export const filtersGeoNone = (_) => {
    return {};
};
export const filtersGeoCircle = (mapView) => {
    const geo = assertGeographic(mapView.center);
    return {
        'lat': geo.latitude,
        'lng': geo.longitude,
        // At zoom 15 the visible area is roughly 3km across -> 1500m radius; Each zoom level halves/doubles the scale.
        'radius': 1500 * Math.pow(2, mapView.zoom),
    };
};
export const filtersGeoRectangle = (mapView, extent) => {
    const geo = extent !== null && extent !== void 0 ? extent : assertGeographic(mapView.extent);
    return {
        'latmin': geo.ymin,
        'latmax': geo.ymax,
        'lngmin': geo.xmin,
        'lngmax': geo.xmax,
        'zoom': Math.round(mapView.zoom),
    };
};
export const useViewportLoad = ({ mapView, mapReady, pageSize = 1000, fetchFn, filtersDefault, filtersGeo, filters: overrides, loadKey, }) => {
    const itemsMapRef = React.useRef(new Map());
    const [items, setItems] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);
    const loadIdRef = React.useRef(0);
    const debounceRef = React.useRef(null);
    const coverageRef = React.useRef(null);
    const updateFromMap = React.useCallback(() => {
        setItems(Array.from(itemsMapRef.current.values()));
    }, []);
    const fetchForViewport = React.useCallback((mapView) => __awaiter(void 0, void 0, void 0, function* () {
        const loadId = ++loadIdRef.current;
        setIsLoading(true);
        try {
            const extent = requestExtent(mapView);
            const filters = Object.assign(Object.assign(Object.assign({}, filtersDefault), filtersGeo(mapView, extent)), overrides); // { lat, lng, radius, sortBy: 'distance', page: 1, pageSize, ...filterOverrides }
            const firstPage = yield fetchFn(filters);
            if (loadId !== loadIdRef.current)
                return;
            const fresh = new Map();
            for (const item of firstPage.items)
                fresh.set(item.id, item);
            setTotal(firstPage.total);
            // Fetch remaining pages
            for (let page = 2; page <= firstPage.totalPages; page++) {
                const result = yield fetchFn(Object.assign(Object.assign({}, filters), { page }));
                if (loadId !== loadIdRef.current)
                    return;
                for (const item of result.items)
                    fresh.set(item.id, item);
            }
            const zoom = Math.round(mapView.zoom);
            coverageRef.current = {
                extent,
                zoom,
                'pointMode': isPointMode(extent, zoom),
                'filters': overrides,
                'loadKey': loadKey === null || loadKey === void 0 ? void 0 : loadKey(mapView),
            };
            // A new array identity rebuilds the map layers and refetches the sidebar.
            const changed = !sameKeys(itemsMapRef.current, fresh);
            itemsMapRef.current = fresh;
            if (changed)
                updateFromMap();
        }
        catch (error) {
            console.error('Error loading nearby properties:', error);
        }
        finally {
            if (loadId === loadIdRef.current)
                setIsLoading(false);
        }
    }), [pageSize, updateFromMap, fetchFn, overrides]);
    const triggerFetch = React.useCallback(() => {
        if (!mapView)
            return;
        if (!mapView.center)
            return;
        if (!mapView.extent)
            return;
        const covered = isCovered(coverageRef.current, assertGeographic(mapView.extent), Math.round(mapView.zoom), overrides, loadKey === null || loadKey === void 0 ? void 0 : loadKey(mapView));
        if (!covered)
            fetchForViewport(mapView);
    }, [mapView, fetchForViewport, overrides, loadKey]);
    // Watch for view extent changes (pan/zoom)
    React.useEffect(() => {
        if (!mapView || !mapReady)
            return;
        // Initial fetch once map is ready
        mapView.when(() => triggerFetch());
        const handle = mapView.watch('stationary', (stationary) => {
            if (!stationary)
                return;
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => triggerFetch(), FETCH_DEBOUNCE_MS);
        });
        return () => {
            handle.remove();
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
        };
    }, [mapView, mapReady, triggerFetch]);
    const upsert = React.useCallback((item) => {
        itemsMapRef.current.set(item.id, item);
        updateFromMap();
    }, [updateFromMap]);
    const remove = React.useCallback((id) => {
        itemsMapRef.current.delete(id);
        updateFromMap();
    }, [updateFromMap]);
    const reload = React.useCallback(() => {
        coverageRef.current = null;
        triggerFetch();
    }, [triggerFetch]);
    return { items, total, isLoading, upsert, remove, reload };
};
